import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

function Block({ title, accent, children, testid, aside }) {
  return (
    <section className="px-6 py-20 md:py-28 border-t border-white/10" data-testid={testid}>
      <div className="max-w-[1400px] mx-auto grid gap-10 lg:grid-cols-12">
        <h2 className="lg:col-span-5 font-display text-[#f4efe6] uppercase text-4xl md:text-6xl tracking-tight leading-[0.95]">
          {title} <span className="font-script text-[#00b8ff] normal-case italic">{accent}</span>
        </h2>
        <div className="lg:col-span-7 text-[#cfc8bb] text-base md:text-lg leading-relaxed space-y-5">
          {children}
          {aside}
        </div>
      </div>
    </section>
  );
}

const TextLink = ({ to, children, testid }) => (
  <Link to={to} className="group inline-flex items-center gap-2 text-[#f4efe6] font-semibold border-b border-[#00b8ff] pb-1 hover:text-[#00b8ff] transition-colors duration-200" data-testid={testid}>
    {children}<ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
  </Link>
);

const Chips = ({ items }) => (
  <ul className="flex flex-wrap gap-2.5 pt-2">
    {items.map((t) => <li key={t} className="rounded-full border border-white/15 px-4 py-2 text-sm text-[#f4efe6]">{t}</li>)}
  </ul>
);

export function AudienceIntro() {
  return (
    <div className="grid gap-8 md:grid-cols-2 mb-14 text-[#cfc8bb] text-base md:text-lg leading-relaxed" data-testid="production-audience-intro">
      <div>
        <h3 className="font-display text-[#f4efe6] uppercase text-2xl tracking-tight mb-3">For brands &amp; companies</h3>
        <p>
          Team apparel, launch merch, event merch and retail-ready product. We work to your brand
          guidelines, produce in-house so quality stays consistent from order to order, and ship nationwide to your
          office, your event or straight to your people. <Link to="/who-we-serve/brands-companies" className="text-[#00b8ff] hover:underline">Merch for brands &amp; companies</Link>.
        </p>
      </div>
      <div>
        <h3 className="font-display text-[#f4efe6] uppercase text-2xl tracking-tight mb-3">For artists, bands &amp; new clothing brands</h3>
        <p>
          Tour merch, album drops and your first collection. With low minimums you can test a design with a
          small run, then scale when it sells. We can help with artwork, samples and fulfillment so you can focus on
          the music or the brand. See <Link to="/artist-merch" className="text-[#00b8ff] hover:underline">artist merch</Link> and{' '}
          <Link to="/who-we-serve/clothing-brands" className="text-[#00b8ff] hover:underline">clothing brands</Link>.
        </p>
      </div>
    </div>
  );
}

export function MinimumsSection() {
  return (
    <Block title="Minimums &" accent="turnaround" testid="production-minimums" aside={<TextLink to="/pricing" testid="production-pricing-link">See how pricing works</TextLink>}>
      <p>
        <strong className="text-[#f4efe6]">Low minimums.</strong> Screen printing, embroidery, DTG, DTF, sublimation
        and print-on-demand have no minimum — one hoodie for a prototype, a short run for a pop-up or thousands of
        tees for a tour. Cut and sew starts at 50 pieces per style / 50 pieces per color. It all runs through the same
        in-house production in Los Angeles / Orange County.
      </p>
      <p>
        <strong className="text-[#f4efe6]">Typical turnaround is 7–10 days.</strong> Your quote confirms the timeline
        for your order. Working to a hard date — a show, a launch, an event? Put your deadline on the quote form and
        we will plan production around it.
      </p>
      <p>
        Pricing depends on the technique, garment, quantity and the number of colors or placements, so every order
        gets its own quote. Small runs are welcome — on most techniques you can order exactly what you
        need instead of rounding up.
      </p>
    </Block>
  );
}

export function BlanksSection() {
  return (
    <Block title="Blanks &" accent="materials" testid="production-blanks">
      <p>
        We stock all major blank brands, so you can choose the fit, weight and fabric that suits your product —
        from everyday tees to heavyweight hoodies, hats, totes and jackets.
      </p>
      <p>
        Want something no blank can give you? Our cut and sew production builds garments from the fabric up, with
        your own pattern, fit and details.
      </p>
      <p>
        Not sure which blank to pick? Tell us how the piece will be used — stage, retail, giveaway or uniform — and
        your budget, and we&apos;ll suggest options that fit, along with the decoration method that works best on
        that fabric.
      </p>
      <Chips items={['T-shirts', 'Hoodies & sweats', 'Hats', 'Totes', 'Jackets', 'Custom cut and sew']} />
    </Block>
  );
}

export function FulfillmentSection() {
  return (
    <Block title="Fulfillment &" accent="e-commerce" testid="production-fulfillment">
      <p>
        Production is only half the job. We can take your merch the rest of the way, so you don&apos;t have to store
        boxes or pack orders yourself.
      </p>
      <Chips items={['Online store setup', 'Storage', 'Pick, pack & ship', 'Tour restocks']} />
      <p>
        Whether you sell online, on the road or both, orders ship nationwide from our Los Angeles / Orange County
        base — and restocks can follow your tour.
      </p>
      <p>
        Because production and fulfillment are handled in the same place, you have one partner from the first print
        to the last delivery instead of coordinating between a printer and a warehouse.
      </p>
    </Block>
  );
}
